// context/CartContext.js
import React, { createContext, useContext, useState } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({});
  const [orderNotes, setOrderNotes] = useState('');

  const addToCart = (item) => {
    // item puede ser: { product, quantity, productNotes } o simplemente el producto
    const product = item.product || item;
    const quantity = item.quantity || 1;
    const productNotes = item.productNotes || '';

    setCart((prevCart) => {
      const existingItem = prevCart[product.id];
      if (existingItem) {
        return {
          ...prevCart,
          [product.id]: {
            ...existingItem,
            quantity: existingItem.quantity + quantity,
          },
        };
      } else {
        return {
          ...prevCart,
          [product.id]: { product, quantity, productNotes },
        };
      }
    });
  };

  const updateCartQuantity = (productId, newQuantity) => {
    setCart((prevCart) => {
      if (newQuantity <= 0) {
        const newCart = { ...prevCart };
        delete newCart[productId];
        return newCart;
      }
      return {
        ...prevCart,
        [productId]: {
          ...prevCart[productId],
          quantity: newQuantity,
        },
      };
    });
  };

  const handleProductNotesChange = (productId, notes) => {
    setCart((prevCart) => ({
      ...prevCart,
      [productId]: {
        ...prevCart[productId],
        productNotes: notes,
      },
    }));
  };

  const clearCart = () => {
    setCart({});
    setOrderNotes('');
  };

  const calculateTotal = () => {
    return Object.values(cart)
      .reduce((acc, item) => acc + item.product.price * item.quantity, 0)
      .toFixed(2);
  };

  const getCartItems = () => {
    return Object.values(cart);
  };

  const getCartCount = () => {
    return Object.keys(cart).length;
  };

  const value = {
    cart,
    orderNotes,
    setOrderNotes,
    addToCart,
    updateCartQuantity,
    handleProductNotesChange,
    clearCart,
    total: calculateTotal(),
    cartItems: getCartItems(),
    cartCount: getCartCount(),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;